import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";
import { formatLatexPreview } from "../../../../components/inputs/FractionInput";

function formatFraction(numerator, denominator) {
  const gcd = (a, b) => (b === 0 ? Math.abs(a) : gcd(b, a % b));

  const sign = Math.sign(numerator) * Math.sign(denominator);
  const absNum = Math.abs(numerator);
  const absDen = Math.abs(denominator);
  const d = gcd(absNum, absDen);

  const simpNum = absNum / d;
  const simpDen = absDen / d;

  if (simpNum % simpDen === 0) {
    return `${sign * (simpNum / simpDen)}`;
  }

  const whole = Math.floor(simpNum / simpDen);
  const remainder = simpNum % simpDen;

  if (whole === 0) {
    return `${sign < 0 ? "-" : ""}\\dfrac{${simpNum}}{${simpDen}}`;
  }

  return `${sign < 0 ? "-" : ""}${whole}\\dfrac{${remainder}}{${simpDen}}`;
}

function formatLineLatex(aLatex, bLatex, bValue) {
  const aPart = aLatex === "1" ? "x" : aLatex === "-1" ? "-x" : `${aLatex}x`;

  if (bValue === 0) {
    return `y = ${aPart}`;
  }

  if (aLatex === "0") {
    return `y = ${bLatex}`;
  }

  return `y = ${aPart} ${bValue >= 0 ? "+" : "-"} ${bLatex.replace("-", "")}`;
}

function formatLineLatexFromUser(aParsed, aRaw, bParsed, bRaw) {
  if (!Number.isFinite(aParsed) || !Number.isFinite(bParsed)) return null;

  const aLatexRaw = String(formatLatexPreview(aRaw || ""));
  const bLatexRaw = String(formatLatexPreview(bRaw || ""));
  const aLatex = aLatexRaw.includes("=") ? aLatexRaw.split("=")[1].trim() : aLatexRaw;
  const bLatex = bLatexRaw.includes("=") ? bLatexRaw.split("=")[1].trim() : bLatexRaw;

  if (aParsed === 0) {
    return `y = ${bLatex}`;
  }

  return formatLineLatex(aParsed === 1 ? "1" : aParsed === -1 ? "-1" : aLatex, bLatex, bParsed);
}

export function generateTask() {
  const rand = () => Math.floor(Math.random() * 17) - 8;
  let x1, y1, x2, y2;

  do {
    x1 = rand();
    y1 = rand();
    x2 = rand();
    y2 = rand();
  } while (x1 === x2);

  const dx = x2 - x1;
  const dy = y2 - y1;

  const gcdSlope = (a, b) => b === 0 ? Math.abs(a) : gcdSlope(b, a % b);
  const slopeGcd = gcdSlope(dy, dx);
  const simpNum = dy / slopeGcd;
  const simpDen = dx / slopeGcd;

  const bNum = y1 * simpDen - simpNum * x1;
  const bDen = simpDen;
  const bGcd = gcdSlope(bNum, bDen);
  const bSimpNum = bNum / bGcd;
  const bSimpDen = bDen / bGcd;

  return {
    type: "FindLineTwoPointsTask",
    data: {
      x1, y1, x2, y2,
      slope: { numerator: dy, denominator: dx },
      b: { numerator: bSimpNum, denominator: bSimpDen }
    }
  };
}

export function renderTask({ data }) {
  const { x1, y1, x2, y2 } = data;
  return (
    <>
      <p>Wyznacz równanie prostej w postaci kierunkowej <InlineMath math="y = ax + b" />, która przechodzi przez punkty:</p>
      <p>
        <InlineMath math={`A=(${x1},${y1})`} />,{" "}
        <InlineMath math={`B=(${x2},${y2})`} />
      </p>
      <p>W szczególności oblicz współczynnik kierunkowy <InlineMath math="a" /> oraz wyraz wolny <InlineMath math="b" />.</p>
    </>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  const ua = userAnswer ?? {};

  return (
    <div className="flex gap-4 items-center flex-wrap">
      <div className="flex items-center gap-2">
        <span><InlineMath math="a = " /></span>
        <FractionInput
          value={ua.a || ""}
          onChange={(val) => onChange({ ...ua, a: val })}
          disabled={disabled}
          label="a"
        />
      </div>
      <div className="flex items-center gap-2">
        <span><InlineMath math="b = " /></span>
        <FractionInput
          value={ua.b || ""}
          onChange={(val) => onChange({ ...ua, b: val })}
          disabled={disabled}
          label="b"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const aUser = userAnswer.a?.parsed;
  const bUser = userAnswer.b?.parsed;
  if (aUser == null || bUser == null) return false;

  const aExpected = data.slope.numerator / data.slope.denominator;
  const bExpected = data.b.numerator / data.b.denominator;

  return Math.abs(aExpected - aUser) < 0.01 && Math.abs(bExpected - bUser) < 0.01;
}

export function renderUserAnswer(userAnswer) {
  if (
    !userAnswer ||
    typeof userAnswer !== "object" ||
    !userAnswer.a?.raw ||
    !userAnswer.b?.raw
  ) {
    return <i>(brak odpowiedzi)</i>;
  }

  const aValue = userAnswer.a?.parsed;
  const bValue = userAnswer.b?.parsed;
  const userLineLatex = formatLineLatexFromUser(aValue, userAnswer.a.raw, bValue, userAnswer.b.raw);

  return (
    <div className="space-y-4 mt-4" style={{ textAlign: "left" }}>
      <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
        <InlineMath math={formatLatexPreview(userAnswer.a.raw, "a")} />
      </div>
      <div style={{ marginBottom: "1rem" }}>
        <InlineMath math={formatLatexPreview(userAnswer.b.raw, "b")} />
      </div>
      {userLineLatex ? (
        <>
          <strong>Równanie prostej z Twojej odpowiedzi:</strong>
          <div style={{ display: "block", textAlign: "left", marginTop: "0.5rem" }}>
            <InlineMath math={`\\displaystyle ${userLineLatex}`} />
          </div>
        </>
      ) : null}
    </div>
  );
}

export function renderAnswer({ data }) {
  const { numerator, denominator } = data.slope;
  const { numerator: bNum, denominator: bDen } = data.b;

  const gcd = (a, b) => (b === 0 ? Math.abs(a) : gcd(b, a % b));
  const slopeGcd = gcd(numerator, denominator);
  const simpNum = numerator / slopeGcd;
  const simpDen = denominator / slopeGcd;
  const aLatex = formatFraction(simpNum, simpDen);

  const b = bNum / bDen;
  const bLatex = formatFraction(bNum, bDen);

  return (
    <div className="space-y-4 mt-4" style={{ textAlign: "left" }}>
      <div style={{ marginTop: "1rem", marginBottom: "1rem" }}>
        <InlineMath math={`a = ${aLatex}`} />
      </div>
      <div style={{ marginBottom: "1rem" }}>
        <InlineMath math={`b = ${bLatex}`} />
      </div>
      <strong>Równanie prostej ma postać:</strong>
      <div style={{ display: "block", textAlign: "left", marginTop: "0.5rem" }}>
        <InlineMath math={`\\displaystyle ${formatLineLatex(aLatex, bLatex, b)}`} />
      </div>
    </div>
  );
}

export function renderExplanation({ data }) {
  const { x1, y1, x2, y2 } = data;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const fs = (v) => (v < 0 ? `(${v})` : v);

  const gcd = (a, b) => (b === 0 ? Math.abs(a) : gcd(b, a % b));
  const d = gcd(dy, dx);
  const simpNum = dy / d;
  const simpDen = dx / d;
  const aLatex = formatFraction(simpNum, simpDen);

  const bNum = y1 * simpDen - simpNum * x1;
  const bDen = simpDen;
  const bGcd = gcd(bNum, bDen);
  const bSimpNum = bNum / bGcd;
  const bSimpDen = bDen / bGcd;
  const b = bSimpNum / bSimpDen;
  const bLatex = formatFraction(bSimpNum, bSimpDen);

  const axNum = simpNum * x1;
  const axDen = simpDen;
  const axGcd = gcd(axNum, axDen);
  const axSimpNum = axNum / axGcd;
  const axSimpDen = axDen / axGcd;
  const axLatex = formatFraction(axSimpNum, axSimpDen);

  return (
    <div className="space-y-3">
      <p><strong>Sposób 1:</strong> Najpierw liczymy współczynnik kierunkowy, a potem wyraz wolny.</p>

      <p><strong>Krok 1:</strong> Obliczamy współczynnik kierunkowy prostej przechodzącej przez punkty A i B:</p>
      <BlockMath math={"a = \\frac{y_B - y_A}{x_B - x_A}"} />
      <BlockMath math={`a = \\frac{${fs(y2)} - ${fs(y1)}}{${fs(x2)} - ${fs(x1)}} = \\frac{${dy}}{${dx}} = ${aLatex}`} />

      <p><strong>Krok 2:</strong> Obliczamy wyraz wolny <InlineMath math="b" /> ze wzoru:</p>
      <BlockMath math="b = y - ax" />
      <p>Podstawiamy współrzędne punktu <InlineMath math={`A = (${x1}, ${y1})`} /> oraz <InlineMath math={`a = ${aLatex}`} />:</p>
      <BlockMath
        math={`b = ${fs(y1)} - (${aLatex} \\cdot ${fs(x1)}) = ${fs(y1)} - ${axNum < 0 ? `(${axLatex})` : axLatex} = ${bLatex}`}
      />

      <p><strong>Krok 3:</strong> Równanie prostej w postaci kierunkowej:</p>
      <BlockMath math={formatLineLatex(aLatex, bLatex, b)} />

      <p style={{ marginTop: 22 }}>
        <strong>Alternatywny sposób: układ równań z dwóch punktów.</strong>
      </p>
      <p>
        Skoro prosta ma postać <InlineMath math="y=ax+b" />, to współrzędne
        punktów <InlineMath math="A" /> i <InlineMath math="B" /> muszą spełniać
        ten sam wzór:
      </p>
      <BlockMath
        math={`\\begin{cases}
${fs(y1)} = a\\cdot ${fs(x1)} + b \\\\
${fs(y2)} = a\\cdot ${fs(x2)} + b
\\end{cases}`}
      />
      <p>Z pierwszego równania wyznaczamy <InlineMath math="b" />:</p>
      <BlockMath math={`b = ${fs(y1)} - a\\cdot ${fs(x1)}`} />
      <p>Wstawiamy ten zapis do drugiego równania:</p>
      <BlockMath
        math={`${fs(y2)} = a\\cdot ${fs(x2)} + ${fs(y1)} - a\\cdot ${fs(x1)}`}
      />
      <BlockMath
        math={`${fs(y2)} - ${fs(y1)} = a\\left(${fs(x2)} - ${fs(x1)}\\right)`}
      />
      <BlockMath
        math={`a = \\frac{${fs(y2)} - ${fs(y1)}}{${fs(x2)} - ${fs(x1)}} = ${aLatex}`}
      />
      <p>
        Potem podstawiamy <InlineMath math={`a=${aLatex}`} /> do wzoru na{" "}
        <InlineMath math="b" />:
      </p>
      <BlockMath math={`b = ${fs(y1)} - ${aLatex}\\cdot ${fs(x1)} = ${bLatex}`} />
      <BlockMath math={formatLineLatex(aLatex, bLatex, b)} />
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
